import { useEffect, useRef, useState } from 'react';
import { Switch } from '@components/forms';
import Loader from '@components/widgets/Loader';
import {
  fetchMfaEnabled,
  getMfaErrorMessage,
  getMfaSuccessMessage,
  isMfaSettingEnvironment,
  updateMfaSetting
} from '../mfaSettingUtils';

type MfaSettingPanelProps = {
  autoApplyEnabled?: boolean | null;
  showTitle?: boolean;
};

const MfaSettingPanel = ({
  autoApplyEnabled = null,
  showTitle = true
}: MfaSettingPanelProps) => {
  const isMfaSettingAllowed = isMfaSettingEnvironment();
  const [isMfaEnabled, setIsMfaEnabled] = useState(false);
  const [isMfaLoading, setIsMfaLoading] = useState(true);
  const [isMfaSaving, setIsMfaSaving] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [mfaSuccessMessage, setMfaSuccessMessage] = useState('');
  const hasAutoAppliedRef = useRef(false);

  const applyMfaSetting = async (checked: boolean) => {
    setIsMfaSaving(true);
    setMfaError('');
    setMfaSuccessMessage('');

    try {
      await updateMfaSetting(checked);
      setIsMfaEnabled(checked);
      setMfaSuccessMessage(getMfaSuccessMessage(checked));
      window.setTimeout(() => setMfaSuccessMessage(''), 5000);
    } catch (error) {
      console.error(error);
      setMfaError(getMfaErrorMessage(error));
    } finally {
      setIsMfaSaving(false);
    }
  };

  const handleMfaToggle = async (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    await applyMfaSetting(checked);
  };

  useEffect(() => {
    let isMounted = true;

    const loadMfaSetting = async () => {
      if (!isMfaSettingAllowed) {
        setIsMfaLoading(false);
        return;
      }

      try {
        const currentEnabled = await fetchMfaEnabled();
        if (!isMounted) {
          return;
        }

        if (typeof currentEnabled === 'boolean') {
          setIsMfaEnabled(currentEnabled);
        }

        if (
          autoApplyEnabled !== null &&
          !hasAutoAppliedRef.current &&
          currentEnabled !== autoApplyEnabled
        ) {
          hasAutoAppliedRef.current = true;
          await applyMfaSetting(autoApplyEnabled);
        }
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setMfaError(getMfaErrorMessage(error));
        }
      } finally {
        if (isMounted) {
          setIsMfaLoading(false);
        }
      }
    };

    loadMfaSetting();

    return () => {
      isMounted = false;
    };
  }, [autoApplyEnabled, isMfaSettingAllowed]);

  if (!isMfaSettingAllowed) {
    return null;
  }

  if (isMfaLoading) {
    return <Loader bgColor="bg-gray-50" bgOpacity="bg-opacity-100" />;
  }

  return (
    <div style={{ maxWidth: '520px' }} className="p-2">
      {showTitle && (
        <h1 className="font-semibold text-xl text-gray-900 mb-6">Security</h1>
      )}
      <p className="text-[16px] text-gray-700 mb-6">
        Protect your account with multi-factor authentication (MFA). When
        enabled, you&apos;ll be asked for a code from your authenticator app
        each time you sign in.
      </p>
      <div
        className="mb-6 flex items-center justify-between gap-4"
        style={{ maxWidth: '520px' }}>
        <div>
          <label className="block font-medium text-gray-700 text-sm">
            Multi-factor authentication
          </label>
          <p className="text-sm text-gray-500 mt-1">
            {isMfaEnabled
              ? 'MFA is currently enabled for your account.'
              : 'MFA is currently disabled for your account.'}
          </p>
        </div>
        <Switch
          checked={isMfaEnabled}
          onChange={handleMfaToggle}
          disabled={isMfaSaving}
        />
      </div>
      {mfaError && <p className="text-xs text-error-light mb-6">{mfaError}</p>}
      {mfaSuccessMessage && (
        <p className="text-success-700 font-medium text-sm mb-6">
          {mfaSuccessMessage}
        </p>
      )}
    </div>
  );
};

export default MfaSettingPanel;
